import React, { useEffect, useState } from "react"
import styled from 'styled-components';
import ReCAPTCHA from 'react-google-recaptcha';
import CurrencyInput from 'react-currency-input-field';

const Separator = styled.div`
  position: relative;
  overflow: hidden;
  text-align: center;
  width: 100%;
  margin: 15px 0;

  span {
      display: inline-block;
      vertical-align: baseline;
      zoom: 1;
      *display: inline;
      *vertical-align: auto;
      position: relative;
      padding: 0 20px;

      &:before, &:after {
          content: '';
          display: block;
          width: 1000px;
          position: absolute;
          top: 0.73em;
          border-top: 1px solid black;
      }

      &:before { right: 100%; }
      &:after { left: 100%; }
  }
`;

const InputsContainer = styled.div`
  display: flex;
  justify-content: space-between;

  & > div {
    flex: 1;

    &:first-child {
      margin-right: 10px;
    }
  }
`;

const SubmitButton = styled.button`
  margin-left: auto;
  margin-right: auto;
  margin-bottom: 10px;

  &:disabled {
    background-color: #ccc;
    border-color: #ccc;
  }
`;

const NumberIput = styled.input`
  -moz-appearance: textfield;

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;

interface SearchFormProps {
  onIdChange?(groupId: string | null): void
}

export function SearchForm(props: SearchFormProps) {
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceAmount, setInvoiceAmount] = useState<string | undefined>('');
  const [memberId, seMmemberId] = useState('');
  const [lastName, setLastName] = useState('');
  const [formValid, setFormValid] = useState(false);
  const [loading, setLoading] = useState(false);
  const [generalError, setGeneralError] = useState('');
  const [captchaToken, setCaptchaToken] = useState('');
  const recaptchaRef = React.useRef<ReCAPTCHA>(null);

  const handleInvoiceNumberInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInvoiceNumber(e.target.value);
  };

  const hanldeInvoiceAmountInput = (value: string | undefined) => {
    if (!value || Number.isNaN(Number(value))) {
      setInvoiceAmount('');
      return;
    }

    setInvoiceAmount(value);
  };

  const handleMemberIdInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    seMmemberId(e.target.value);
  };

  const handleLastNameInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLastName(e.target.value);
  };

  useEffect(() => {
    setFormValid(((!!invoiceNumber && !!invoiceAmount) || (!!memberId && !!lastName)) && !!captchaToken);
  }, [invoiceNumber, invoiceAmount, memberId, lastName, captchaToken]);

  const handleCatpcha = (token: string | null) => {
    setCaptchaToken(token || '');
  };

  const handleSubmit = async (evt: React.FormEvent<HTMLFormElement>) => {
    evt.preventDefault();
    evt.stopPropagation();
    setLoading(true);
    setGeneralError('');
    const result = await fetch(`${process.env.REACT_APP_BACKEND_URL}/ags/one-time-payment/search`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        invoiceNumber,
        invoiceAmount,
        memberId,
        lastName,
        captcha: captchaToken
      })
    });
    setLoading(false);
    const json = await result.json();
    if (json.error && json.message) {
      setGeneralError(json.message);
      if (json.message.includes('captcha')) {
        recaptchaRef.current?.reset();
        setCaptchaToken('');
      }
      return;
    }

    if (result.ok && json.id) {
      if (props.onIdChange) {
        props.onIdChange(json.id);
      }
      return;
    }

    setGeneralError('No records found');
    recaptchaRef.current?.reset();
    setCaptchaToken('');
  }

  return (
    <div style={{paddingTop: '15px'}}>
      <form onSubmit={handleSubmit}>
        <InputsContainer>
          <div>
            <label htmlFor="invoiceNumber">Invoice number:</label><br />
            <NumberIput type="number" id="invoiceNumber" name="invoiceNumber" onChange={handleInvoiceNumberInput} value={invoiceNumber} /><br />
          </div>
          <div>
            <label htmlFor="invoiceAmount">Invoice amount:</label><br />
            <CurrencyInput
              id="invoiceAmount"
              name="invoiceAmount"
              onValueChange={hanldeInvoiceAmountInput}
              intlConfig={{ locale: 'en-US', currency: 'USD' }}
              allowNegativeValue={false}
              disableAbbreviations={false}
              maxLength={11}
              prefix="$ "
              value={invoiceAmount} />
            <br />
          </div>
        </InputsContainer>
        <Separator><span>OR</span></Separator>
        <InputsContainer>
          <div>
            <label htmlFor="memberId">Member ID:</label><br />
            <NumberIput type="number" id="memberId" name="memberId" onChange={handleMemberIdInput} value={memberId} /><br />
          </div>
          <div>
            <label htmlFor="lastName">Last name:</label><br />
            <input type="text" id="lastName" name="lastName" onChange={handleLastNameInput} value={lastName} />
          </div>
        </InputsContainer>
        <div style={{display: 'flex', justifyContent: 'center', marginTop: '15px'}}>
          <ReCAPTCHA
            sitekey={process.env.REACT_APP_RECAPTCHA_KEY || 'fakeKey'}
            onChange={handleCatpcha}
            ref={recaptchaRef}
          />
        </div>
        <div style={{textAlign: 'center', marginTop: '15px'}}>
          <SubmitButton type="submit" disabled={!formValid || loading}>{loading ? 'Loading...' : 'Search'}</SubmitButton>
          {generalError && <p style={{color: 'red', fontWeight: 'bold'}}>{generalError}</p>}
        </div>
      </form>
    </div>
  )
}


// invoice number & amount
// member id and last name
